import { createAsyncThunk } from "@reduxjs/toolkit";
import APICaller from "../../utils/APICaller";
import HandleSearch from "../../utils/CommonSearchComponent";

/**
 * DEFAULT DYNAMIC THUNK
 *
 * @param {string} typePrefix   Redux action prefix
 * @param {string} endpoint     API endpoint
 * @param {string} method       HTTP method (POST | GET | PUT | DELETE)
 */
export const createDynamicThunk = ({
  typePrefix,
  endpoint = "",
  method = "POST",
  withPagination = false,
  useHandleSearch = false,
  responseType = "json",
}) => {
  return createAsyncThunk(
    typePrefix,
    async (payload = {}, { getState, rejectWithValue }) => {
      try {
        const { auth } = getState();

        const { role, accessToken, lIUId, accessTokenExpiresOn } = auth;
        /**
         *  CASE 1: HandleSearch based listing
         */

        if (useHandleSearch) {
          const {
            searchIn,
            callActionFrom = "",
            hasSearchedValue = "",
            searchKeyword = "",
            pagination = {
              current: 1,
              page: 1,
              pageSize: 50,
            },
          } = payload;

          // HandleSearch must RETURN data
          const response = await HandleSearch(
            hasSearchedValue,
            searchIn,
            searchKeyword,
            callActionFrom,
            {
              role,
              accessToken,
              lIUId,
              accessTokenExpiresOn,
            },
            { pagination },
          );

         

          /**
           * Expected HandleSearch return:
           * {
           *   data: [],
           *   totalCount: number
           * }
           */

          return {
            data: response?.data || [],
            pagination: {
              ...pagination,
              total: response?.totalCount || 0,
            },
            totalCount: response?.totalCount || 0,
            meta: response?.meta ?? null,
          };
        }

        /**
         *  CASE 2: Normal API call
         */
        let finalEndpoint = payload?.endpointOverride || endpoint;
        let dataToSend = payload;

        // Handle FormData
        if (!(payload instanceof FormData)) {
          const { id, endpointOverride, headers, ...rest } = payload || {};
          dataToSend = rest;
          if (id) finalEndpoint = `${finalEndpoint}/${id}`;
        }

        const finalMethod = payload?.id && method === "POST" ? "PUT" : method;
        const headers = payload?.headers ?? null;
        const tokenToUse = payload?.auth || accessToken;
        const response = await APICaller(
          role,
          tokenToUse,
          finalEndpoint,
          finalMethod,
          finalMethod === "GET" ? null : dataToSend,
          responseType === "blob" ? "blob" : accessTokenExpiresOn,
          headers,
        );

        if (responseType === "blob") {
          return response;
        }
        if (withPagination) {
          return {
            data: response?.data ?? [],
            pagination: response?.pagination ?? null,
            totalCount: response?.totalCount ?? null,
            meta: response?.meta ?? null,
          };
        }

        const normalizedResponse =
          typeof response === "string" ? JSON.parse(response) : response;

        return normalizedResponse;
      } catch (error) {
        return rejectWithValue(
          error?.response?.data || { message: error.message },
        );
      }
    },
  );
};
